import { MutableRefObject, useCallback, useEffect, useRef } from "react";

import { MapState } from "@/sx-layout/hooks/useMapState";

type Area = {
  top: number;
  left: number;
  width: number;
  height: number;
  rotation?: number;
};

type Props = {
  selectedDetail: Area;
  updateSelectedDetail: (area: Area) => void;
  areaRef: MutableRefObject<HTMLDivElement>;
  imageRef: MutableRefObject<HTMLImageElement>;
  mapState: MapState;
  setIsDragging: (isDragging: boolean) => void;
  clipAspectRatio?: number;
};

export const useMoveablePlotPlanDetail = ({
  selectedDetail,
  updateSelectedDetail,
  areaRef,
  imageRef,
  mapState,
  setIsDragging,
  clipAspectRatio,
}: Props) => {
  const { scale, mapRatio } = mapState;
  const resizeStartAreaRef = useRef<Area | null>(null);
  const dragStartAreaRef = useRef<Area | null>(null);
  const moveableRef = useRef<any>(null);

  useEffect(() => {
    if (moveableRef.current) {
      moveableRef.current.updateRect();
    }
  }, [selectedDetail, scale]);

  // target(areaRef.current)が変更されたときにMoveableを更新する
  useEffect(() => {
    if (moveableRef.current && areaRef.current) {
      moveableRef.current.updateRect();
    }
  }, [areaRef.current]);

  const onDragStart = useCallback(() => {
    dragStartAreaRef.current = selectedDetail;
  }, [selectedDetail]);

  const onDrag = useCallback(
    ({ beforeTranslate }) => {
      if (!imageRef.current || !dragStartAreaRef.current || !areaRef.current) return;

      const target = areaRef.current;
      const imageRect = imageRef.current.getBoundingClientRect();
      const startArea = dragStartAreaRef.current;

      let x = beforeTranslate[0];
      let y = beforeTranslate[1];

      // PlotMap.tsx と同様のロジック: getBoundingClientRect() の結果に scale(1/zoomScale) を掛けてズーム前のサイズを得る
      const imageWidth = imageRect.width * scale;
      const imageHeight = imageRect.height * scale;

      const currentLeft = startArea.left * mapRatio;
      const currentTop = startArea.top * mapRatio;
      const currentWidth = startArea.width * mapRatio;
      const currentHeight = startArea.height * mapRatio;

      if (currentLeft + x < 0) {
        x = -currentLeft;
      } else if (currentLeft + x + currentWidth > imageWidth) {
        x = imageWidth - currentLeft - currentWidth;
      }

      if (currentTop + y < 0) {
        y = -currentTop;
      } else if (currentTop + y + currentHeight > imageHeight) {
        y = imageHeight - currentTop - currentHeight;
      }

      target.style.transform = `translate(${x}px, ${y}px) rotate(${selectedDetail.rotation ?? 0}deg)`;
      setIsDragging(true);
    },
    [imageRef, areaRef, scale, mapRatio, setIsDragging, selectedDetail.rotation]
  );

  const onDragEnd = useCallback(
    ({ isDrag }) => {
      if (!isDrag || !areaRef.current) {
        setIsDragging(false);
        dragStartAreaRef.current = null;
        return;
      }

      const target = areaRef.current;
      const style = window.getComputedStyle(target);
      const matrix = new DOMMatrixReadOnly(style.transform);
      const translateX = matrix.m41;
      const translateY = matrix.m42;

      const newLeft = Math.round((selectedDetail.left * mapRatio + translateX) / mapRatio);
      const newTop = Math.round((selectedDetail.top * mapRatio + translateY) / mapRatio);

      if (newLeft !== selectedDetail.left || newTop !== selectedDetail.top) {
        updateSelectedDetail({
          ...selectedDetail,
          left: newLeft,
          top: newTop,
        });
      }

      requestAnimationFrame(() => {
        target.style.transform = `rotate(${selectedDetail.rotation ?? 0}deg)`;
        if (moveableRef.current) {
          moveableRef.current.updateRect();
        }
      });
      setIsDragging(false);
      dragStartAreaRef.current = null;
    },
    [areaRef, selectedDetail, mapRatio, updateSelectedDetail, setIsDragging]
  );

  const onResizeStart = useCallback(() => {
    resizeStartAreaRef.current = selectedDetail;
  }, [selectedDetail]);

  const onResize = useCallback(
    ({ width, height, direction, drag, inputEvent }) => {
      if (!imageRef.current || !resizeStartAreaRef.current || !areaRef.current) return;

      const target = areaRef.current;
      const startArea = resizeStartAreaRef.current;
      const imageRect = imageRef.current.getBoundingClientRect();
      const isShiftKey = inputEvent?.shiftKey ?? false;

      // PlotMap.tsx と同様のロジック: getBoundingClientRect() の結果に scale(1/zoomScale) を掛けてズーム前のサイズを得る
      const imageWidth = imageRect.width * scale;
      const imageHeight = imageRect.height * scale;
      const currentLeft = startArea.left * mapRatio;
      const currentTop = startArea.top * mapRatio;
      const startAreaWidth = startArea.width * mapRatio;
      const startAreaHeight = startArea.height * mapRatio;
      const minSize = 20 * mapRatio;

      // Shiftキー押下時: clip_w/clip_h のオリジナルアスペクト比を維持しながらリサイズ
      const effectiveAspectRatio =
        clipAspectRatio != null && clipAspectRatio > 0
          ? clipAspectRatio
          : startArea.width > 0 && startArea.height > 0
          ? startArea.width / startArea.height
          : undefined;
      let state: ResizeState = { w: width, h: height, x: drag.beforeTranslate[0], y: drag.beforeTranslate[1] };

      if (isShiftKey && effectiveAspectRatio != null) {
        // Shiftキー押下時: アスペクト比を維持しながらリサイズ

        // オリジナルのアスペクト比（clip_w/clip_h）を維持するよう w/h を調整する
        let { w, h, x, y } = state;
        if (Math.abs(direction[0]) === 1 && direction[1] === 0) {
          // 横方向のみのリサイズ: 幅に合わせて高さを決める
          h = w / effectiveAspectRatio;
        } else if (direction[0] === 0 && Math.abs(direction[1]) === 1) {
          // 縦方向のみのリサイズ: 高さに合わせて幅を決める
          w = h * effectiveAspectRatio;
        } else {
          // 斜め方向: 変化量が大きい軸を基準にする
          const wRatio = w / startAreaWidth;
          const hRatio = h / startAreaHeight;
          if (wRatio >= hRatio) {
            h = w / effectiveAspectRatio;
          } else {
            w = h * effectiveAspectRatio;
          }
        }
        state = { w, h, x, y };

        // 画像範囲外への拡大を防ぐ（クランプ）
        const clamped = clampToImageBounds(
          state,
          direction,
          currentLeft,
          currentTop,
          startAreaWidth,
          startAreaHeight,
          imageWidth,
          imageHeight,
          minSize
        );

        // 単軸リサイズ時にアスペクト比固定で変化した他軸が画像範囲を超えないようにクランプする
        ({ w, h, x, y } = clamped);
        if (direction[1] === 0 && direction[0] !== 0) {
          // 横方向リサイズ: hが変化しているので上下境界をチェック
          const newTop = currentTop + y;
          if (newTop < 0) {
            h = h + newTop;
            y = -currentTop;
            w = h * effectiveAspectRatio;
            if (direction[0] === -1) x = startAreaWidth - w;
          } else if (newTop + h > imageHeight) {
            h = imageHeight - newTop;
            w = h * effectiveAspectRatio;
            if (direction[0] === -1) x = startAreaWidth - w;
          }
        } else if (direction[0] === 0 && direction[1] !== 0) {
          // 縦方向リサイズ: wが変化しているので左右境界をチェック
          const newLeft = currentLeft + x;
          if (newLeft < 0) {
            w = w + newLeft;
            x = -currentLeft;
            h = w / effectiveAspectRatio;
            if (direction[1] === -1) y = startAreaHeight - h;
          } else if (newLeft + w > imageWidth) {
            w = imageWidth - newLeft;
            h = w / effectiveAspectRatio;
            if (direction[1] === -1) y = startAreaHeight - h;
          }
        }
        state = { w, h, x, y };
      } else {
        // 通常リサイズ（Shiftキーなし）
        // 画像範囲外への拡大を防ぐ（クランプ）
        state = clampToImageBounds(
          state,
          direction,
          currentLeft,
          currentTop,
          startAreaWidth,
          startAreaHeight,
          imageWidth,
          imageHeight,
          minSize
        );
      }

      const { w, h, x, y } = state;
      target.style.width = `${Math.round(w)}px`;
      target.style.height = `${Math.round(h)}px`;
      target.style.transform = `translate(${x}px, ${y}px) rotate(${selectedDetail.rotation ?? 0}deg)`;
      setIsDragging(true);
    },
    [imageRef, areaRef, scale, mapRatio, setIsDragging, selectedDetail.rotation, clipAspectRatio]
  );

  const onResizeEnd = useCallback(
    ({ isDrag, inputEvent }) => {
      const isShiftKey = inputEvent?.shiftKey ?? false;
      if (!isDrag || !areaRef.current) {
        setIsDragging(false);
        resizeStartAreaRef.current = null;
        return;
      }

      const target = areaRef.current;
      const style = window.getComputedStyle(target);
      const matrix = new DOMMatrixReadOnly(style.transform);
      const translateX = matrix.m41;
      const translateY = matrix.m42;
      const width = parseInt(target.style.width);
      const height = parseInt(target.style.height);

      const newLeft = Math.round((selectedDetail.left * mapRatio + translateX) / mapRatio);
      const newTop = Math.round((selectedDetail.top * mapRatio + translateY) / mapRatio);
      let newWidth = Math.round(width / mapRatio);
      let newHeight = Math.round(height / mapRatio);

      // shiftキーを押しながらリサイズ操作をしたときはclip_w/clip_hのオリジナルアスペクト比を基準にリサイズする
      const startArea = resizeStartAreaRef.current;
      if (isShiftKey && clipAspectRatio != null && clipAspectRatio > 0 && startArea) {
        newHeight = Math.round(newWidth / clipAspectRatio);
      } else if (isShiftKey && startArea && startArea.width > 0 && startArea.height > 0) {
        const scaleX = newWidth / startArea.width;
        const scaleY = newHeight / startArea.height;
        const uniformScale = (scaleX + scaleY) / 2;
        newWidth = Math.round(startArea.width * uniformScale);
        newHeight = Math.round(startArea.height * uniformScale);
      }

      if (
        newLeft !== selectedDetail.left ||
        newTop !== selectedDetail.top ||
        newWidth !== selectedDetail.width ||
        newHeight !== selectedDetail.height
      ) {
        updateSelectedDetail({
          ...selectedDetail,
          left: newLeft,
          top: newTop,
          width: newWidth,
          height: newHeight,
        });
      }

      requestAnimationFrame(() => {
        target.style.transform = `rotate(${selectedDetail.rotation ?? 0}deg)`;
        if (moveableRef.current) {
          moveableRef.current.updateRect();
        }
      });
      setIsDragging(false);
      resizeStartAreaRef.current = null;
    },
    [areaRef, selectedDetail, mapRatio, updateSelectedDetail, setIsDragging, clipAspectRatio]
  );

  const onRotate = useCallback(
    ({ transform }) => {
      if (!areaRef.current) return;
      areaRef.current.style.transform = transform;
      setIsDragging(true);
    },
    [areaRef, setIsDragging]
  );

  const onRotateEnd = useCallback(
    ({ isDrag, lastEvent }) => {
      if (!isDrag || !lastEvent) {
        setIsDragging(false);
        return;
      }

      const rawRotation = lastEvent.rotate;
      const rotation = ((((rawRotation + 180) % 360) + 360) % 360) - 180;
      updateSelectedDetail({
        ...selectedDetail,
        rotation,
      });

      setIsDragging(false);
    },
    [selectedDetail, updateSelectedDetail, setIsDragging]
  );

  return {
    onDrag,
    onDragEnd,
    onDragStart,
    onResizeStart,
    onResize,
    onResizeEnd,
    onRotate,
    onRotateEnd,
    moveableRef,
  };
};

type ResizeState = {
  w: number;
  h: number;
  x: number;
  y: number;
};

/** 各軸の境界クランプを行い、クランプが発生したかを返す */
const clampToImageBounds = (
  state: ResizeState,
  direction: number[],
  currentLeft: number,
  currentTop: number,
  startAreaWidth: number,
  startAreaHeight: number,
  imageWidth: number,
  imageHeight: number,
  minSize: number
): ResizeState => {
  let { w, h, x, y } = state;

  if (direction[0] === -1) {
    if (currentLeft + x < 0) {
      x = -currentLeft;
      w = startAreaWidth + currentLeft;
    } else if (w < minSize) {
      w = minSize;
      x = startAreaWidth - minSize;
    }
  } else if (direction[0] === 1) {
    if (currentLeft + w > imageWidth) {
      w = imageWidth - currentLeft;
    } else if (w < minSize) {
      w = minSize;
    }
  }

  if (direction[1] === -1) {
    if (currentTop + y < 0) {
      y = -currentTop;
      h = startAreaHeight + currentTop;
    } else if (h < minSize) {
      h = minSize;
      y = startAreaHeight - minSize;
    }
  } else if (direction[1] === 1) {
    if (currentTop + h > imageHeight) {
      h = imageHeight - currentTop;
    } else if (h < minSize) {
      h = minSize;
    }
  }

  return { w, h, x, y };
};
